// KYC Test Component
// This component allows testing the region-based KYC system

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { shouldShowKYC, getKYCMessage } from '../utils/kycUtils';
import KYCStatusIndicator from './KYCStatusIndicator';

const KYCTestComponent = () => {
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [userProfile, setUserProfile] = useState({ country: 'US', countryCode: 'US' });
  const [kycStatus, setKycStatus] = useState({
    verified: false,
    status: 'not_started'
  });
  const [kycInfo, setKycInfo] = useState({ show: false, required: false, reason: '' });
  const [kycMessage, setKycMessage] = useState('');

  const handleCountryChange = async (event) => {
    const country = event.target.value;
    setSelectedCountry(country);
    const profile = { country, countryCode: country };
    setUserProfile(profile);
    
    // Fetch KYC info from database
    const info = await shouldShowKYC(profile);
    const message = await getKYCMessage(profile);
    setKycInfo(info);
    setKycMessage(message);
  };

  useEffect(() => {
    const loadKYCInfo = async () => {
      const info = await shouldShowKYC(userProfile);
      const message = await getKYCMessage(userProfile);
      setKycInfo(info);
      setKycMessage(message);
    };
    loadKYCInfo();
  }, [userProfile]);

  const testCountries = [
    { code: 'US', name: 'United States', region: 'North America' },
    { code: 'GB', name: 'United Kingdom', region: 'Europe' },
    { code: 'DE', name: 'Germany', region: 'Europe' },
    { code: 'FR', name: 'France', region: 'Europe' },
    { code: 'PK', name: 'Pakistan', region: 'South Asia' },
    { code: 'IN', name: 'India', region: 'South Asia' },
    { code: 'BD', name: 'Bangladesh', region: 'South Asia' },
    { code: 'AU', name: 'Australia', region: 'Oceania' },
    { code: 'CA', name: 'Canada', region: 'North America' },
    { code: 'JP', name: 'Japan', region: 'Asia' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        KYC Region Testing
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Different Regions
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Country</InputLabel>
                <Select
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  label="Select Country"
                >
                  {testCountries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name} ({country.code}) - {country.region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity={kycInfo.required ? "warning" : "info"} sx={{ mb: 2 }}>
                <strong>KYC Status:</strong> {kycInfo.required ? 'Required' : 'Not Required'}
                <br />
                <strong>Reason:</strong> {kycInfo.reason}
                <br />
                <strong>Country:</strong> {userProfile.country}
              </Alert>

              <Typography variant="body2" color="text.secondary">
                {kycMessage}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                KYC Status Indicator
              </Typography>
              
              {kycInfo.show ? (
                <KYCStatusIndicator 
                  userProfile={userProfile}
                  kycStatus={kycStatus}
                  onStartKYC={() => alert('Starting KYC process...')}
                />
              ) : (
                <Alert severity="success">
                  KYC is not required for users in {userProfile.country}. 
                  The KYC menu item and status indicator will be hidden.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Region Configuration
              </Typography>
              
              <Alert severity="info">
                <Typography variant="body2">
                  KYC requirements are now fetched from the database (main.CountryKYCRequirements table).
                  Use the admin panel to manage country-specific KYC requirements.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KYCTestComponent;






























